
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useContacts } from "@/hooks/useContacts";
import { 
  Loader2, Mail, Phone, Building, GitMerge, 
  Trash, PencilLine, FileText, Activity, MoreVertical
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { RelationshipHealthCard } from "@/components/contact/RelationshipHealthCard";
import { RelationshipInteractions } from "@/components/contact/RelationshipInteractions";
import { ContactScoringCard } from "@/components/contact/ContactScoringCard";
import { ContactActivityFeed } from "@/components/contact/ContactActivityFeed";
import { EnhancedGiftTrackingCard } from "@/components/gift/EnhancedGiftTrackingCard";
import { DeleteContactDialog } from "@/components/contact/DeleteContactDialog";
import { MergeContactDialog } from "@/components/contact/MergeContactDialog";
import { ActionMenu, ActionItem } from "@/components/ui/action-menu";
import { useGiftTracking } from "@/hooks/useGiftTracking";
import { useRelationshipHealth } from "@/hooks/useRelationshipHealth";

export default function ContactDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getContactById, deleteContact, mergeContacts } = useContacts();
  const { getContactGiftRecords } = useGiftTracking();
  const { getContactHealth } = useRelationshipHealth();
  const { toast } = useToast();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);

  // Fetch contact data
  const { 
    data: contact, 
    isLoading: isLoadingContact,
    error: contactError 
  } = getContactById(id!);

  // Fetch gift records for this contact
  const {
    data: giftRecords = [],
    isLoading: isLoadingGifts
  } = getContactGiftRecords(id!);

  // Fetch relationship health data
  const {
    data: healthData,
    isLoading: isLoadingHealth
  } = getContactHealth(id!);

  useEffect(() => {
    if (contactError) {
      toast({
        title: "Error",
        description: "Failed to load contact details. The contact may have been deleted.",
        variant: "destructive"
      });
      navigate("/contacts");
    }
  }, [contactError, toast, navigate]);

  const handleDeleteContact = async (contactId: string) => {
    try {
      await deleteContact(contactId);
      navigate("/contacts");
    } catch (error: any) {
      // Error handling is done in the DeleteContactDialog component
      throw error;
    }
  };

  const handleMergeContacts = async (sourceId: string, targetId: string) => {
    try {
      await mergeContacts({ sourceId, targetId });
      navigate("/contacts");
    } catch (error: any) {
      // Error handling is done in the MergeContactDialog component
      throw error;
    }
  };

  const contactActions: ActionItem[] = [
    {
      label: "Edit Contact",
      icon: <PencilLine className="h-4 w-4" />,
      href: `/contacts/${id}/edit`
    },
    {
      label: "Log Activity",
      icon: <Activity className="h-4 w-4" />,
      href: `/activities/create?contact_id=${id}`
    },
    {
      label: "Merge Contact",
      icon: <GitMerge className="h-4 w-4" />,
      onClick: () => setIsMergeDialogOpen(true)
    },
    {
      label: "Delete Contact",
      icon: <Trash className="h-4 w-4" />,
      onClick: () => setIsDeleteDialogOpen(true),
      variant: "destructive"
    }
  ];

  if (isLoadingContact) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!contact) return null;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            {contact.first_name} {contact.last_name}
          </h1>
          <p className="text-muted-foreground">{contact.job_title || "No title"}</p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to={`/contacts/${id}/edit`}>
              <PencilLine className="h-4 w-4 mr-2" />
              Edit
            </Link>
          </Button>
          <Button 
            variant="destructive" 
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            <Trash className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <ActionMenu 
            label="Contact Actions"
            actions={contactActions}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact card */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span>{contact.email || "No email address"}</span>
                </div>
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span>{contact.phone || "No phone number"}</span>
                </div>
                <div className="flex items-start">
                  <Building className="h-4 w-4 mr-2 text-muted-foreground" />
                  <div>
                    {contact.organization ? (
                      <Link
                        to={`/organizations/${contact.organization.id}`}
                        className="hover:underline text-primary"
                      >
                        {contact.organization.name}
                      </Link>
                    ) : (
                      "No organization"
                    )}
                    <p className="text-sm text-muted-foreground">
                      {contact.department || "No department"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-border">
                <h4 className="text-sm font-medium mb-1">Communication</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Preferred Method:</span>
                    <span className="capitalize">{contact.preferred_contact_method || "Not set"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Frequency:</span>
                    <span className="capitalize">{contact.contact_frequency || "Not set"}</span>
                  </div>
                  {contact.last_contacted_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Last Contacted:</span>
                      <span>{formatDate(contact.last_contacted_date)}</span>
                    </div>
                  )}
                  {contact.next_contact_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Next Contact:</span>
                      <span>{formatDate(contact.next_contact_date)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-2 border-t border-border">
                <h4 className="text-sm font-medium mb-1">Additional Details</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Influence Level:</span>
                    <span className="capitalize">{contact.influence_level || "Not set"}</span>
                  </div>
                  {contact.birthday && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Birthday:</span>
                      <span>{formatDate(contact.birthday)}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
            <CardFooter className="border-t flex justify-between pt-4">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/contacts">Back to Contacts</Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/activities/create?contact_id=${contact.id}`}>
                  <Activity className="h-4 w-4 mr-1" />
                  Log Activity
                </Link>
              </Button>
            </CardFooter>
          </Card>

          {/* Contact Scoring Card */}
          <ContactScoringCard contactId={id!} />
          
          {/* Relationship Health Card */}
          <RelationshipHealthCard contactId={id!} />
        </div>

        {/* Main content area */}
        <div className="md:col-span-2">
          <Tabs defaultValue="activity">
            <TabsList>
              <TabsTrigger value="activity">Activity Feed</TabsTrigger>
              <TabsTrigger value="interactions">Interactions</TabsTrigger>
              <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
              <TabsTrigger value="gifts">Gifts</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>

            <div className="mt-4">
              <TabsContent value="activity">
                <ContactActivityFeed contactId={id!} showScore={false} />
              </TabsContent>
              
              <TabsContent value="interactions">
                <RelationshipInteractions contactId={id!} />
              </TabsContent>

              <TabsContent value="opportunities">
                <Card>
                  <CardHeader>
                    <CardTitle>Opportunities</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-8">
                      <FileText className="mx-auto h-10 w-10 text-muted-foreground opacity-50" />
                      <h3 className="mt-2 font-medium">No opportunities</h3>
                      <p className="mt-1 text-muted-foreground">
                        There are no opportunities associated with this contact yet.
                      </p>
                      <Button className="mt-4" asChild>
                        <Link to={`/opportunities/create?contact=${id}`}>Create Opportunity</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="gifts">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Gifts & Tokens of Appreciation</CardTitle>
                      <Button size="sm" asChild>
                        <Link to="/gifts/create">Send Gift</Link>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingGifts ? (
                      <div className="text-center py-4">Loading gift history...</div>
                    ) : giftRecords.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="mx-auto h-10 w-10 text-muted-foreground opacity-50" />
                        <h3 className="mt-2 font-medium">No gifts sent</h3>
                        <p className="mt-1 text-muted-foreground">
                          No gifts have been sent to this contact yet.
                        </p>
                        <Button className="mt-4" asChild>
                          <Link to="/gifts/create">Send First Gift</Link>
                        </Button>
                      </div>
                    ) : (
                      <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
                        {giftRecords.map(gift => (
                          <EnhancedGiftTrackingCard
                            key={gift.id}
                            gift={gift}
                            showContact={false}
                            showOrganization={true}
                          />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="notes">
                <Card>
                  <CardHeader>
                    <CardTitle>Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-8">
                      <FileText className="mx-auto h-10 w-10 text-muted-foreground opacity-50" />
                      <h3 className="mt-2 font-medium">No notes yet</h3>
                      <p className="mt-1 text-muted-foreground">
                        There are no notes associated with this contact.
                      </p>
                      <Button className="mt-4">Add Note</Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      {isDeleteDialogOpen && (
        <DeleteContactDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          contact={contact}
          onDelete={handleDeleteContact}
          redirectAfterDelete={true}
        />
      )}

      {isMergeDialogOpen && (
        <MergeContactDialog
          isOpen={isMergeDialogOpen}
          onClose={() => setIsMergeDialogOpen(false)}
          contact={contact}
          onMerge={handleMergeContacts}
        />
      )}
    </div>
  );
}
